import { LayoutPreset } from '../presets/layoutPresets';
import { CanvasBackground } from '../backgrounds/canvasBackgrounds';

export interface ITemplateRow {
  id: string;
  title: string;
  category: 'education' | 'business' | 'content' | 'developer' | 'interview' | 'custom';
  scenes: ITemplateScene[];
  scripts: ITemplateScript[];
  metadata: {
    duration: number;
    sceneCount: number;
    isOfficial: boolean;
  };
  createdAt: number;
  createdBy?: string;
}

export interface ITemplateScene {
  order: number;
  title: string;
  suggestedDuration: number;
  layoutPreset: string;
  background: CanvasBackground;
  scriptKey: string;
  recordingTips: string[];
}

export interface ITemplateScript {
  key: string;
  sceneTitle: string;
  content: string;
  defaultExample: string;
  customizationNote: string;
  teleprompterSettings: {
    speed: number;
    fontSize: number;
  };
}

export interface ISceneRow {
  id: string;
  videoGuid: string;
  order: number;
  title: string;
  duration: number;
  chunkIds: string[];
  layout: LayoutPreset | string;
  background: CanvasBackground;
  createdAt: number;
  templateId?: string;
}

export interface ITelemetryRow {
  id: string;
  eventType: 
    | 'upload_start'
    | 'upload_complete'
    | 'upload_error'
    | 'chunk_retry'
    | 'network_error'
    | 'worker_crash'
    | 'migration_start'
    | 'migration_complete'
    | 'migration_rollback';
  videoGuid?: string;
  metadata: Record<string, any>;
  timestamp: number;
  userId?: string;
}

export interface IChunkRowV5 {
  id: string;
  videoGuid: string;
  chunkNo: number;
  status: "pending" | "uploading" | "done" | "error";
  size: number;
  blob: ArrayBuffer;
  isLast: boolean;
  uploadId?: string;
  key?: string;
  createdAt: number;
  updatedAt: number;
  templateId?: string;
  sceneId?: string;
}

export interface IUploadRowV5 {
  videoGuid: string;
  status: "initializing" | "active" | "completed" | "error";
  videoId?: string;
  uploadId?: string;
  key?: string;
  uploadedChunks: number;
  parts: Array<{ ETag: string; PartNumber: number; "content-length": string }>;
  createdAt: number;
  updatedAt: number;
  thumbnails?: string[];
  syncQueued?: boolean;
  templateId?: string;
}

export const V5_STORES = {
  chunks:
    "id, videoGuid, status, chunkNo, updatedAt, [videoGuid+chunkNo], [videoGuid+status], templateId, sceneId",
  uploads: "videoGuid, status, createdAt, updatedAt, syncQueued, templateId",
  templates: "id, category, createdAt, [category+createdAt]",
  scenes: "id, videoGuid, order, [videoGuid+order], templateId",
  telemetry: "id, eventType, timestamp, videoGuid, [eventType+timestamp], userId",
};

export const V5_MIGRATION_NOTES = `
IndexedDB v5 Migration Strategy:

1. Shadow Copy Approach:
   - v5 schema created alongside v4
   - v4 remains read-only during migration
   - New writes go to v5
   - v4 dropped after 1 sprint buffer

2. Data Migration:
   - Copy chunks: Add templateId, sceneId fields (nullable)
   - Copy uploads: Add thumbnails, syncQueued, templateId fields (nullable)
   - New tables: templates, scenes, telemetry (empty on migration)

3. Rollback Procedure:
   - Revert code to v4
   - Delete v5 tables
   - Restore from pre-migration backup if needed

4. Zero Data Loss Guarantee:
   - Pre-migration backup (JSON export)
   - Checksum validation (v4 data === v5 data)
   - Progress indicator for user
`;
